import { useEffect, useMemo, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import { QueryClientProvider, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import { FormProvider, useForm } from 'react-hook-form';

import {
  createRefundMutation as createRefundMutationOptions,
  getRefundOptions,
  getRefundQueryKey,
  listPaymentMethodsOptions,
  listPaymentsOptions,
  updateRefundMutation as updateRefundMutationOptions,
} from '@drivingdoc/api-client/generated/react-query';
import { Payment, PaymentMethod, Refund } from 'types/finance';

import DateModified from 'components/Dialogs/components/DateModified';
import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import SelectInput, { SelectInputOption } from 'components/Forms/SelectInput';
import TextInput from 'components/Forms/TextInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { getZodObjectDefaults } from 'utils/getZodObjectDefaults';

import RefundAllocationTable from './components/RefundAllocationTable';
import RefundSummary from './components/RefundSummary';
import { RefundFormSchema, RefundFormValues } from './RefundSchema';

// interface DialogProps {
//   onClose?: () => void;
// }

// Creating a new payment
export type NewRefundDialogParams = {
  refundId?: null | undefined;
  studentId: string; // required if no paymentId
};

// Editing an existing payment
export type UpdateRefundDialogParams = {
  refundId: string;
  studentId?: never; // disallow studentId when editing existing payment
};

// Union for new or existing charge
export type OpenRefundDialogParams = NewRefundDialogParams | UpdateRefundDialogParams;

export default function useRefundDialog({ payload: dialogState, open, onClose }: DialogProps<OpenRefundDialogParams>) {
  // dialog state
  //   const [dialogState, setDialogState] = useState<OpenRefundDialogParams | null>(
  //     null
  //   );

  // flag to prevent overwriting user changes
  const hasFormInitialized = useRef(false);

  const dialogs = useDialogs();

  const form = useForm<RefundFormValues>({
    mode: 'all',
    resolver: zodResolver(
      RefundFormSchema.transform((data) => ({
        ...data,

        // remove read only fields not required for submission
        allocations: data.allocations.map(({ id, amount }) => ({ id, amount })),
      })),
    ),
  });

  const {
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    trigger,
  } = form;

  const queryClient = useQueryClient();

  // get existing refund information
  const existingRefund = useQuery({
    ...getRefundOptions({
      path: {
        id: dialogState?.refundId ?? '',
      },
    }),
    gcTime: 0,
    enabled: !!dialogState?.refundId,
  });
  const existingRefundData = existingRefund.data as Refund | undefined;

  // get existing payments
  const existingPayments = useQuery({
    ...listPaymentsOptions({
      query: {
        studentId: dialogState?.studentId ?? existingRefundData?.student_user_id ?? '',
      },
    }),
    gcTime: 0,
    enabled: !!dialogState?.studentId || !!existingRefundData?.student_user_id,
  });
  const existingPaymentsData = existingPayments.data as Payment[] | undefined;

  // get payment methods
  const paymentMethods = useQuery(
    listPaymentMethodsOptions({
      query: {
        type: 'all',
      },
    }),
  );
  const paymentMethodsData = paymentMethods.data as PaymentMethod[] | undefined;

  const paymentMethodOptions = useMemo(() => {
    return paymentMethodsData?.reduce<SelectInputOption[]>((options, paymentMethod) => {
      // add the option if it is allowed for refunds
      if (paymentMethod.allow_method_for_refund) {
        // add the option
        return [
          ...options,
          {
            label: paymentMethod.name,
            value: paymentMethod.id,
          },
        ];
      }

      // if it was already selected and not allowed for refunds
      if (existingRefundData?.payment_method_id === paymentMethod.id) {
        // add the option but disable it
        return [
          ...options,
          {
            label: paymentMethod.name,
            value: paymentMethod.id,
            disabled: true,
          },
        ];
      }

      // skip payment method
      return options;
    }, []);
  }, [paymentMethodsData, existingRefundData]);

  const createRefundMutation = useMutation(createRefundMutationOptions());
  const updateRefundMutation = useMutation(updateRefundMutationOptions());

  useEffect(() => {
    // Creating a new refund, reset the form with base defaults:
    if (!dialogState.refundId) {
      form.reset({
        ...getZodObjectDefaults(RefundFormSchema),
        id: null,
        student_user_id: dialogState.studentId,
      });
    }
  }, [dialogState, form]);

  //   const openRefundDialog = (params: OpenRefundDialogParams) => {
  //     setDialogState(params);
  //     hasFormInitialized.current = false;

  //     // Creating a new refund, reset the form with base defaults:
  //     if (!params.refundId) {
  //       form.reset({
  //         ...getZodObjectDefaults(RefundFormSchema),
  //         id: null,
  //         student_user_id: params.studentId,
  //       });
  //     }
  //   };

  const handleClose = () => {
    // close the dialog
    // setDialogState(null);
    form.reset({ id: null, student_user_id: undefined });
    hasFormInitialized.current = false;
    onClose?.();
  };

  const createRefund = async (data: RefundFormValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('Are you sure you want to issue a refund?', {
        cancelText: 'Cancel',
        severity: 'error',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      // create the refund
      const result = await createRefundMutation.mutateAsync({
        body: data,
      });

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the existing refund query
        queryClient.invalidateQueries({
          queryKey: ['refund', { id: dialogState?.refundId }],
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Refund Created',
        });
      }
    } catch (error) {
      // show error message
      dialogs.open(FeedbackDialog, {
        title: 'Error creating refund',
        message: getApiErrorMessage(error),
      });
    }
  };

  const updateRefund = async (data: RefundFormValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('Are you sure you want to update the refund?', {
        cancelText: 'Cancel',
        severity: 'error',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      // update the refund
      if (!data.id) {
        throw new Error('Refund id is required');
      }

      const result = await updateRefundMutation.mutateAsync({
        body: data,
        path: {
          id: data.id,
        },
      });

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the existing refund query
        if (dialogState?.refundId) {
          queryClient.invalidateQueries({
            queryKey: getRefundQueryKey({
              path: {
                id: dialogState.refundId,
              },
            }),
          });
        }

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Refund Updated',
        });
      }
    } catch (error) {
      // show error message
      dialogs.open(FeedbackDialog, {
        title: 'Error updating refund',
        message: getApiErrorMessage(error),
      });
    }
  };

  const onSubmit = (formData: RefundFormValues) => {
    // remove fields not required for submission
    // const submissionData = {
    //   ...formData,
    //   allocations: formData.allocations.map(({ id, amount }) => ({
    //     id,
    //     amount,
    //   })),
    // };

    console.log('Refund Form Data:', formData);
    // create or update the refund
    if (dialogState?.refundId) {
      updateRefund(formData);
    } else {
      createRefund(formData);
    }
  };

  // reset allocations handler
  const handleResetAllocations = () => {
    // get the latest allocations from form state
    const currentAllocations = getValues('allocations');

    // reset "amount" back to "previous_amount" or 0 if creating new refund
    const resetAllocations = currentAllocations?.map((alloc) => ({
      ...alloc,
      amount: alloc.previous_amount ?? 0,
    }));

    // set it back in the form state, causing re-render
    setValue('allocations', resetAllocations, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    // trigger custom validation logic
    trigger('allocations');
  };

  // get refund allocations from existing payments and previously refunded payments
  const getRefundAllocations = (payments: Payment[], refund?: Refund): RefundFormValues['allocations'] => {
    // create map of previously refunded amounts
    const allocationMap = new Map<string, number>();
    for (const alloc of refund?.student_payment_refunds ?? []) {
      allocationMap.set(alloc.student_payment_id, alloc.amount);
    }

    // filter out voided payments and sort by date
    const nonVoidPayments = payments.filter((payment) => !payment.voided).sort((a, b) => a.date.localeCompare(b.date));

    // create form allocations from relevant payments
    // - if the payment hasn't already been fully refunded
    // - or if there's an existing allocation from this refund.
    const paymentAllocations: RefundFormValues['allocations'] = nonVoidPayments
      .map((payment) => {
        const paymentAmountAllocated = Number(payment.amount_allocated);
        const paymentAmountRefunded = Number(payment.amount_refunded);

        return {
          id: payment.id,
          amount: allocationMap.get(payment.id) ?? 0,
          previous_amount: allocationMap.get(payment.id) ?? 0,
          memo: payment.memo ?? '',
          payment_amount: payment.amount,
          payment_amount_allocated: paymentAmountAllocated,
          payment_amount_refunded: paymentAmountRefunded,
        };
      })
      .filter((alloc) => alloc.payment_amount_refunded < alloc.payment_amount || alloc.previous_amount > 0);

    // return the payment allocations
    return paymentAllocations;
  };

  useEffect(() => {
    // if the form is already initialized, skip
    if (hasFormInitialized.current) return;

    // wait for payments to load
    if (!existingPayments.isSuccess || !existingPaymentsData) return;

    // if a new refund is being created
    if (!dialogState?.refundId) {
      const allocations = getRefundAllocations(existingPaymentsData);

      // only reset the allocations so we don't overwrite user-changed fields
      form.reset((prev) => ({
        ...prev,
        allocations,
      }));
    } else {
      // wait for existingRefund
      if (!existingRefund.isSuccess || !existingRefundData) return;

      const allocations = getRefundAllocations(existingPaymentsData, existingRefundData);

      form.reset({
        id: existingRefundData.id,
        student_user_id: existingRefundData.student_user_id,
        date: existingRefundData.date,
        // amount: existingRefund.data.amount,
        memo: existingRefundData.memo ?? '',
        description: existingRefundData.description,
        private_notes: existingRefundData.private_notes,
        payment_method_id: existingRefundData.payment_method_id ?? undefined,
        check_no: existingRefundData.check_no,
        allocations,
      });
    }

    // mark as initialized
    hasFormInitialized.current = true;
  }, [
    existingRefund.isSuccess,
    existingPayments.isSuccess,
    existingRefundData,
    existingPaymentsData,
    form,
    dialogState?.refundId,
  ]);

  useEffect(() => {
    // show an error dialog if there was an issue fetching the existing refund data
    if (existingRefund?.isError) {
      dialogs.open(FeedbackDialog, {
        title: 'Something went wrong while fetching the existing refund info.',
        message: existingRefund?.error?.message ?? '',
      });
    }
  }, [dialogs, existingRefund?.error?.message, existingRefund?.isError]);

  useEffect(() => {
    console.log('Form Errors:', errors);
  }, [errors]);

  useEffect(() => {
    console.log('Refund Dialog State:', dialogState);
  }, [dialogState]);

  const RefundDialog = (
    <QueryClientProvider client={queryClient}>
      <Dialog fullWidth open={open} onClose={handleClose} maxWidth="md">
        <DialogTitle component="div" display="flex" justifyContent="space-between">
          {dialogState?.refundId ? 'Update Refund' : 'Issue A Refund'}
        </DialogTitle>

        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormProvider {...form}>
            <DialogContent>
              <Grid container rowSpacing={3} columnSpacing={1}>
                {/* <Grid size={{ xs: 12, md: 4 }}>
                                    <CurrencyInput
                                        name="amount"
                                        label="Total Amount"
                                        textFieldProps={{
                                            autoComplete: 'off',
                                        }}
                                    />
                                </Grid> */}

                <Grid size={{ xs: 12, md: 8 }}>
                  <SelectInput
                    name="payment_method_id"
                    label="Refund Method"
                    autoComplete="off"
                    // options={paymentMethods.data?.map(paymentMethod => ({
                    //     label: paymentMethod.name,
                    //     value: paymentMethod.id
                    // }))}
                    options={paymentMethodOptions}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextInput
                    name="check_no"
                    label="Check Number"
                    helperText="Optional"
                    autoComplete="off"
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start">#</InputAdornment>,
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                </Grid>

                <Grid size={12}>
                  <TextInput name="memo" label="Memo" autoComplete="off" />
                </Grid>

                <Grid size={12}>
                  <TextInput
                    name="description"
                    label="Description"
                    multiline
                    helperText={<span style={{ color: 'red' }}>Description visible to customer.</span>}
                    autoComplete="off"
                  />
                </Grid>

                <Grid size={12}>
                  <TextInput
                    name="private_notes"
                    label="Private Notes"
                    multiline
                    helperText="Private notes are only visible to administrators."
                    autoComplete="off"
                  />
                </Grid>

                <Grid size={12}>
                  <RefundAllocationTable
                    resetAllocations={handleResetAllocations}
                    // autoAllocatePayment={handleAutoAllocation}
                  />
                </Grid>

                <Grid size={12}>
                  <RefundSummary />
                </Grid>

                <Grid size={12}>
                  <Box display="flex" flexDirection="column" rowGap={1}>
                    <DateModified
                      prefix="Created: "
                      date={existingRefundData?.created_at}
                      user={existingRefundData?.created_by_user}
                    />
                    <DateModified
                      prefix="Updated: "
                      date={existingRefundData?.updated_at}
                      user={existingRefundData?.updated_by_user}
                    />
                    <DateModified
                      prefix="Voided: "
                      date={existingRefundData?.voided_at}
                      user={existingRefundData?.voided_by_user}
                    />
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions
              sx={{
                flexWrap: 'wrap',
                rowGap: 2,
              }}
            >
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="submit">{dialogState?.refundId ? 'Update' : 'Issue Refund'}</Button>
            </DialogActions>
          </FormProvider>
        </form>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={
            existingRefund.isLoading ||
            existingPayments.isLoading ||
            createRefundMutation.isPending /* || updatePaymentMutation.isLoading*/
          }
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Dialog>
    </QueryClientProvider>
  );

  return RefundDialog;
}
